<template>
  <div
    class="p-4 sm:p-6 rounded-lg text-lg sm:text-xl text-left relative"
    :class="
      open
        ? 'bg-gray-300 text-gray-700'
        : completed
        ? 'cursor-not-allowed bg-gray-800 hover:bg-gray-600 text-gray-200 hover:text-white'
        : locked
        ? 'cursor-not-allowed bg-gray-200 hover:bg-gray-100 text-gray-500 hover:text-400'
        : pastdue
        ? 'cursor-pointer bg-gray-200 hover:bg-gray-100 text-gray-500 hover:text-400'
        : 'cursor-pointer bg-gray-400 hover:bg-gray-500 text-gray-700 hover:text-gray-800'
    "
    @click.stop="open ? '' : toggleInvoice"
    :style="
      open ? 'background: linear-gradient(150deg, #cbd5e0, #e2e8f0);' : ''
    "
  >
    <!-- :style="open ? 'background: linear-gradient(98deg, #423691, #1d78d4);' : ''" -->
    <div
      class="w-full flex items-center font-normal"
      :class="open ? 'cursor-pointer' : ''"
      @click.stop="toggleInvoice"
    >
      <div class="w-12 h-12">
        <unicon v-if="!completed && !locked && !pastdue" name="clipboard" />
        <unicon
          v-if="pastdue"
          name="exclamation-triangle"
          :class="open ? 'text-red-600' : 'text-red-400'"
        />
        <unicon v-if="locked && !completed" name="padlock" />
        <unicon
          v-if="completed"
          name="check"
          :class="open ? 'text-teal-600' : 'text-teal-400'"
        />
      </div>
      <div class="ml-3 md:flex md:flex-grow">
        <div class="md:flex-grow text-2xl sm:text-3xl leading-none font-bold">
          {{ vendorName(vendorId) }}
        </div>
        <div
          class="mt-1 text-left md:text-right font-medium text-base sm:text-lg leading-none"
          :class="open ? '' : ''"
        >
          <!-- :class="open ? 'hidden' : ''" -->
          <div v-if="!locked && !pastdue">Today</div>
          <div v-else>
            {{
              invoice.deliveryDate
                ? invoice.deliveryDate.toMillis()
                : invoice.orderDate.toMillis()
                  | luxon({ input: 'millis', output: 'relative' })
            }}
          </div>
        </div>
      </div>
    </div>
    <transition-expand @afterEnter="afterEnter">
      <!-- <div v-show="open"> -->
      <div v-if="open" class="mt-4 flex flex-wrap relative h-auto">
        <div class="w-1/2 pr-3">
          <label
            class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
            :for="`invoiceNumber_${invoiceId}_${index}`"
          >
            Invoice Number
          </label>
          <input
            class="mt-1 sm:mt-2 py-2 sm:py-3 px-3 sm:px-4 focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            type="text"
            :ref="`invoiceNumber_${invoiceId}_${index}`"
            :id="`invoiceNumber_${invoiceId}_${index}`"
            placeholder="Required"
            v-model.lazy="localInvoiceNumber"
            autocomplete="off"
            @focus.stop="activeChild = -1"
          />
        </div>
        <div class="w-1/2 pl-3 z-100">
          <label
            class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          >
            Invoice Date
          </label>
          <v-date-picker
            v-model.lazy="localInvoiceDate"
            :is-required="true"
            :popover="{
              placement: $screens({ default: 'bottom-end', sm: 'bottom' }),
              positionFixed: true,
              class: 'z-100',
              visibility: 'click'
            }"
          >
            <template #default="{ inputValue, inputEvents }">
              <div>
                <input
                  class="mt-1 sm:mt-2 py-2 sm:py-3 px-3 sm:px-4 z-50 focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 focus:bg-white text-gray-700 border border-gray-200 focus:border-gray-500 rounded leading-tight"
                  type="text"
                  :ref="`invoiceDate_${invoiceId}_${index}`"
                  :id="`invoiceDate_${invoiceId}_${index}`"
                  :value="inputValue"
                  v-on="inputEvents"
                  readonly
                />
              </div>
            </template>
            <!-- :input-props="{
              class:
                'mt-1 sm:mt-2 w-full py-2 sm:py-3 px-3 sm:px-4 z-50 focus:outline-none focus:shadow-outline appearance-none block bg-gray-200 focus:bg-white text-gray-700 border border-gray-200 focus:border-gray-500 rounded leading-tight',
              placeholder: 'Required',
              readonly: true
            }" -->
          </v-date-picker>
        </div>

        <div class="w-full">
          <!-- TODO: add parent component so invoiceItems doesn't force invoice reload and lost activeItem -->
          <!-- <invoice-item
            v-for="(item, itemIndex) in invoiceItems"
            :key="item"
            :index="itemIndex"
            :active-item="activeChild"
            :invoice-id="invoiceId"
            :product-id="item"
            @focused="activeChild = $event"
            class="mt-6 shadow-lg"
          /> -->

          <invoice-items
            :ref="`invoice-items-${invoiceId}`"
            v-if="invoiceItems"
            :items="invoiceItems"
            :active-child="activeChild"
            :invoice-id="invoiceId"
            @focused="activeChild = $event"
          />
        </div>

        <div class="mt-4 w-1/3 pr-2 sm:pr-3">
          <label
            class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
            for="inputFees"
          >
            Fees
          </label>
          <money
            class="mt-1 sm:mt-2 py-2 sm:py-3 px-3 sm:px-4 focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            v-model.lazy="localFees"
            ref="inputFees"
            v-bind="moneyConfig"
            autocomplete="off"
            @blur.native="updateValue('fees')"
            @focus.native.stop="activeChild = -1"
          />
        </div>

        <div class="mt-4 w-1/3 px-2 sm:px-3">
          <label
            class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
            for="inputTax"
          >
            Tax
          </label>
          <money
            class="mt-1 sm:mt-2 py-2 sm:py-3 px-3 sm:px-4 focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            v-model.lazy="localTax"
            ref="inputTax"
            v-bind="moneyConfig"
            autocomplete="off"
            @blur.native="updateValue('tax')"
            @focus.native.stop="activeChild = -1"
          />
        </div>

        <div class="mt-4 w-1/3 pl-2 sm:pl-3">
          <label
            class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
            for="inputTotalCost"
          >
            Total Cost {{ isAdmin ? `(${totalInputCost})` : '' }}
          </label>
          <money
            class="mt-1 sm:mt-2 py-2 sm:py-3 px-3 sm:px-4 focus:outline-none focus:shadow-outline appearance-none block w-full 
              bg-gray-200 focus:bg-white text-gray-700 border border-gray-200 focus:border-gray-500 rounded leading-tight"
            v-model.lazy="localTotalCost"
            ref="inputTotalCost"
            v-bind="{
              decimal: '.',
              thousands: ',',
              prefix: '$ ',
              suffix: '',
              precision: 2
            }"
            autocomplete="off"
            @blur.native="updateValue('totalCost')"
            @focus.native.stop="activeChild = -1"
          />
        </div>
        <!-- <div class="mt-4 w-full">
          <label
            class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
            for="scan"
          >
            Invoice Image
          </label>
          <invoice-scan
            ref="scan"
            v-if="isManager && open && deviceHasMedia"
            class="mt-1 sm:mt-2 py-2 sm:py-3 px-3 sm:px-4 focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            :image-id="imageId"
            @save="saveImage"
          />
        </div> -->

        <div class="mt-6 flex w-full justify-between">
          <s-button
            :class="
              correctTotalValues ? 'shadow-lg' : 'opacity-50 cursor-not-allowed'
            "
            @click="correctTotalValues ? saveInvoice() : ''"
          >
            {{ correctTotalValues ? 'Save' : 'Re-check' }}
          </s-button>
          <s-button
            v-if="productsNotInInvoice.length > 0"
            @click="
              $modal.show('modal--selection', {
                list: productsNotInInvoice,
                display: productName,
                invoiceId,
                sort: true
              })
            "
          >
            Add Item
          </s-button>
        </div>
      </div>
      <!-- </div> -->
    </transition-expand>
    <!-- <modal-selection
      @confirm="{invoiceId, productId} => addProductToInvoice({ invoiceId, productId })"
    /> -->
  </div>
</template>

<script>
import { Money } from 'v-money'
// import InvoiceItem from '@/components/InvoiceItem'
import InvoiceItems from '@/components/InvoiceItems'
// import InvoiceScan from '@/components/InvoiceScan'
import TransitionExpand from '@/components/TransitionExpand'
import SButton from '@/components/Button'
// import ModalSelection from '@/components/ModalSelection'
import { mapGetters, mapActions } from 'vuex'
import { capitalize, floatify } from '@/utils'

// FIX FOCUS ON OPEN/ISACTIVE

export default {
  name: 'Invoice',
  props: {
    invoiceId: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    activeItem: {
      type: Number,
      required: true
    },
    locked: {
      type: Boolean,
      required: false,
      default: false
    },
    pastdue: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      initialLoad: true,
      activeChild: null,
      open: false,
      localTotalCost: 0, // Keep 0, not null, for v-money to work properly
      localFees: 0,
      localTax: 0,
      // localImageId: null,
      // localDownloadURL: null,
      moneyConfig: {
        decimal: '.',
        thousands: ',',
        prefix: '$ ',
        suffix: '',
        precision: 2
      }
    }
  },
  components: {
    // InvoiceItem,
    InvoiceItems,
    SButton,
    // InvoiceScan,
    Money,
    TransitionExpand
    // ModalSelection
  },
  computed: {
    ...mapGetters('user', ['isManager', 'isAdmin']),
    ...mapGetters('storeModule', [
      'productsNotInInvoiceOrZeroedOutByInvoiceId'
    ]),
    ...mapGetters('storeModule/vendors', ['vendorName']),
    ...mapGetters('storeModule/products', [
      'products',
      'isProduct',
      'productName'
    ]),
    ...mapGetters('storeModule/invoices', ['invoiceById']),
    deviceHasMedia() {
      return (
        'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices
      )
    },
    invoice() {
      try {
        return this.invoiceById(this.invoiceId)
      } catch {
        return null
      }
    },
    // imageId() {
    //   try {
    //     return this.invoice.imageId
    //   } catch {
    //     return null
    //   }
    // },
    downloadURL() {
      try {
        return this.invoice.downloadURL
      } catch {
        return null
      }
    },
    vendorId() {
      try {
        return this.invoice.vendorId
      } catch {
        return null
      }
    },
    invoiceNumber() {
      try {
        return this.invoice.invoiceNumber
      } catch {
        return null
      }
    },
    totalCost() {
      try {
        return this.invoice.totalCost
      } catch {
        return null
      }
    },
    invoiceDate() {
      try {
        return this.invoice.invoiceDate
      } catch {
        return null
      }
    },
    tax() {
      try {
        return this.invoice.tax
      } catch {
        return null
      }
    },
    fees() {
      try {
        return this.invoice.fees
      } catch {
        return null
      }
    },
    completed() {
      try {
        return this.invoice.completed
      } catch {
        return null
      }
    },
    isActive() {
      return this.activeItem === this.index
    },
    invoiceItems() {
      if (!this.invoice) return null

      return Object.keys(this.invoice)
        .filter(
          key =>
            // remove amountOrdered for added items?
            this.isProduct(key) &&
            (this.invoice[key].amountOrdered > 0 ||
              this.invoice[key].amountOrdered === false ||
              Object.prototype.hasOwnProperty.call(this.invoice[key], 'action'))
              ? key
              : false
          // this.isProduct(key) ? key : false
        )
        .sort((a, b) =>
          this.productName(a)
            .toLowerCase()
            .localeCompare(this.productName(b).toLowerCase())
        )
    },
    correctTotalValues() {
      return this.totalInputCost === this.localTotalCost //&& this.localImageId
    },
    totalInputCost() {
      if (!this.invoiceItems) return null
      let val = 0
      this.invoiceItems.forEach(item => {
        if (this.invoice[item].totalCost) val += this.invoice[item].totalCost
      })
      val += this.invoice.fees
      val += this.invoice.tax
      return floatify(val)
    },
    productsNotInInvoice() {
      try {
        return this.productsNotInInvoiceOrZeroedOutByInvoiceId(this.invoiceId)
      } catch {
        return []
      }
    },
    productsNotInInvoiceDisplay() {
      try {
        return this.productsNotInInvoiceByInvoiceId(this.invoiceId).map(e =>
          this.productName(e)
        )
      } catch {
        return []
      }
    },
    localInvoiceNumber: {
      get() {
        try {
          return this.invoice.invoiceNumber
        } catch {
          return null
        }
      },
      set(newValue) {
        console.log(newValue)
        if (newValue !== this.invoiceNumber) {
          this.updateItem({
            id: this.invoiceId,
            data: { invoiceNumber: newValue }
          })
        }
      }
    },
    localInvoiceDate: {
      get() {
        try {
          return this.invoice.invoiceDate.toDate()
        } catch {
          return null
        }
      },
      set(newValue) {
        console.log('set localInvoiceDate', newValue)
        // temporary fix - why does this happen?
        if (this.initialLoad) {
          this.initialLoad = false
          // return
        }
        if (newValue != this.invoice.invoiceDate || !this.invoice.invoiceDate) {
          console.log('updating now')
          this.updateItem({
            id: this.invoiceId,
            data: { invoiceDate: newValue }
          })
        }
      }
    }
  },
  watch: {
    // invoice() {
    //   let total = 0
    //   this.invoiceItems.forEach(item => {
    //     total += this.invoice[item]['cost']
    //   })
    //   this.totalInputCost = total
    // },
    activeItem: {
      handler(newValue) {
        console.log('active item change!', this.index, newValue)
      }
    },
    index: {
      handler(newValue) {
        console.log('index change!', this.index, newValue)
      }
    },
    isActive: {
      handler(newValue) {
        if (newValue == false) {
          this.open = false
          this.activeChild = null
        }
      }
    },
    totalCost: {
      immediate: true,
      handler(newValue) {
        if (document.activeElement !== this.$refs.inputTotalCost)
          this.localTotalCost = newValue
      }
    },
    tax: {
      immediate: true,
      handler(newValue) {
        if (document.activeElement !== this.$refs.inputTax)
          this.localTax = newValue
      }
    },
    fees: {
      immediate: true,
      handler(newValue) {
        if (document.activeElement !== this.$refs.inputFees)
          this.localFees = newValue
      }
    }
    // open: {
    //   immediate: true,
    //   handler(newValue) {
    //     // console.dir(this, newValue)
    //   }
    // }
  },
  methods: {
    capitalize,
    floatify,
    ...mapActions('storeModule/invoices', ['updateItem', 'closeInvoice']),
    // saveImage({ id, downloadURL }) {
    //   this.localImageId = id
    //   this.localDownloadURL = downloadURL
    //   this.updateValue('imageId')
    //   this.updateValue('downloadURL')
    // },
    updateValue: function(property) {
      if (this[`local${capitalize(property)}`] !== this[property]) {
        this.updateItem({
          id: this.invoiceId,
          data: { [`${property}`]: this[`local${capitalize(property)}`] }
        })
      }
    },
    setFocus(ref = 'invoiceNumber') {
      if (!this.isActive) return
      console.log('setFocus, isActive=true')
      this.$nextTick(() => {
        console.log('nextTick: this=')
        console.dir(this)
        console.log(`${ref}_${this.invoiceId}_${this.index}`)
        console.dir(this.$refs[`${ref}_${this.invoiceId}_${this.index}`])
        Reflect.has(this.$refs, `${ref}_${this.invoiceId}_${this.index}`) &&
        this.$refs[`${ref}_${this.invoiceId}_${this.index}`]
          ? this.$refs[`${ref}_${this.invoiceId}_${this.index}`].focus()
          : console.log('Missing ref!')
        // try {
        //   this.$refs[`${ref}_${this.invoiceId}_${this.index}`].focus()
        // } catch (error) {
        //   console.log(error)
        // }
      })
    },
    saveInvoice: function() {
      this.activeChild = null
      try {
        this.closeInvoice(this.invoiceId)
      } catch (e) {
        throw Error(e)
      }
      this.toggleInvoice()
    },
    toggleInvoice: function() {
      if (this.completed && !this.isManager) return
      if (this.locked && !this.isManager) return
      this.activeChild = null
      this.$emit('focused', this.index)
      this.open = !this.open
      // console.dir('event: ', event)
      console.log('toggleInvoice: $refs=')
      console.dir(this.$refs)
      this.setFocus()
    },
    afterEnter: function(event) {
      console.log('afterEnter: ', event)
      this.setFocus()
    }
  }
}
</script>
